// University of Kansas: public college page (#2840), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - KU's Common Data Set 2025-2026, posted section by section by
//    Analytics, Institutional Research, and Effectiveness (aire.ku.edu,
//    files C.pdf and G.pdf): C1 applicants, admits and enrollees for Fall
//    2025 with the residency table, C8 test policy, C21/C22 (no early
//    decision, no early action). Its G1/G5 costs are for 2025-2026, so the
//    cost table uses the financial aid office instead.
//  - KU Admissions: Apply to the University of Kansas (assured admission,
//    test policy, fall dates) and Major-Specific Admission Requirements.
//    Neither page names a year. The Freshman Enrollment page dates its next
//    steps to this cycle (enrollment deposits due May 1, 2027; final
//    transcripts by July 1, 2027), so the dates are read as the fall 2027
//    cycle's. KU publishes no application deadline and no decision dates.
//  - KU Financial Aid & Scholarships, 2026-2027 Estimated Cost of
//    Attendance, undergraduate, on campus. Its totals ($33,725 resident,
//    $53,660 non-resident) include a $106 loan fee, left out here. Its
//    non-resident tuition ($31,779, 30 hours at $1,059.30) differs from the
//    $30,779 on the Admissions Costs page, which also uses $2,080
//    transportation and $2,096 personal expenses.
//  - College Scorecard, school id 155317 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'KU Common Data Set 2025-2026',
        url: 'https://aire.ku.edu/common-data-set',
        read: '2026-10-01',
    },
    apply: {
        label: 'KU Admissions, Apply to the University of Kansas',
        url: 'https://admissions.ku.edu/apply',
        read: '2026-10-01',
    },
    majorReq: {
        label: 'KU Admissions, Major-Specific Admission Requirements',
        url: 'https://admissions.ku.edu/major-specific-admission-requirements-university-kansas',
        read: '2026-10-01',
    },
    enrollment: {
        label: 'KU Admissions, Freshman Enrollment',
        url: 'https://admissions.ku.edu/freshman-enrollment',
        read: '2026-10-01',
    },
    coa: {
        label: 'KU Financial Aid & Scholarships, 2026-2027 Estimated Cost of Attendance',
        url: 'https://financialaid.ku.edu/estimated-cost-of-attendance',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?155317-University-of-Kansas',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-kansas',
    kbId: 'university_of_kansas',
    name: 'University of Kansas',
    shortName: 'KU',
    location: 'Lawrence, KS',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (KU brand color palettes, Kansas Blue and Crimson); decoration only.
    branding: {
        primary: '#0051BA',
        secondary: '#E8000D',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_kansas_logo.png',
    },

    // The direct answer and short FAQ (#4090, founder 2026-10-13: content
    // only, no title change before the Oct 27 title-test read). Every figure
    // restates one the page already shows from the source named beside it;
    // CollegePages.test.jsx fails a number the rest of the page does not hold.
    // The search it answers: ku acceptance rate.
    faq: {
        answer: { text: 'The University of Kansas (KU) admitted 92.6% of first-year applicants for fall 2025, 20,541 of the 22,187 who applied, according to its Common Data Set 2025-2026.', source: 'cds', where: 'section C1' },
        questions: [
            { q: 'What is the KU acceptance rate?', a: 'For fall 2025 entry, KU admitted 20,541 of 22,187 first-year applicants, an acceptance rate of 92.6%. Kansas residents were admitted at 94.1% and out-of-state applicants at 91.7%.', source: 'cds', where: 'section C1' },
            { q: 'Who is assured admission to KU?', a: 'You are assured admission to the College of Liberal Arts & Sciences with a cumulative high school GPA of 3.25 or higher, or an ACT of at least 21 or SAT of at least 1060 with a 2.0 GPA. If you meet neither, KU reviews your application individually.', source: 'apply' },
            { q: 'Does KU require the SAT or ACT?', a: 'No. KU is test optional. It highly encourages you to send ACT or SAT scores: they will not hurt your admission eligibility, but may be used in professional school admission decisions.', source: 'apply' },
            { q: 'Does KU have an application deadline?', a: 'KU lists no application deadline for admission. The dates it does list are the freshman scholarship deadline, December 1, and the FAFSA priority deadline, February 1.', source: 'apply' },
            { q: 'Do some KU schools ask for more?', a: 'Yes. Direct admission to the School of Business asks for a 3.4 high school GPA on the test-optional pathway, and the School of Engineering for a 3.0 GPA plus math preparedness. Music needs an audition, and Design a portfolio and essay.', source: 'majorReq' },
        ],
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 22187,
        admitted: 20541,
        enrolled: 5040,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 7479, admitted: 7037 },
            { group: 'Out-of-state', applied: 14011, admitted: 12847 },
            { group: 'International', applied: 697, admitted: 657 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, application data by residency',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'KU\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'KU has no early decision or early action plan.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Assured admission places you in the College of Liberal Arts & Sciences. Direct admission to some schools asks for more, and admission to them is selective:',
        rows: [
            { school: 'School of Business', requirement: 'A 3.4 high school GPA on a 4.0 scale (the test-optional pathway), or an index of your ACT divided by 10 plus your GPA of 5.55 or higher (official scores required).' },
            { school: 'School of Engineering', requirement: 'Admissible to KU, a 3.0 high school GPA, and math preparedness: an ACT math score of 22 (SAT math 540), a B or better in college algebra or higher, a C or better in high school calculus, or a qualifying score on the ALEKS math placement exam.' },
            { school: 'Architecture and Interior Architecture', requirement: 'Guaranteed admission to the 5-year Master of Architecture or Interior Architecture if you apply by November 1 with a 3.8 GPA, or a 3.5 to 3.79 GPA and an ACT 24 (SAT 1160), plus at least one high school drawing or art class with a B or better. Final deadline February 1; later or other applicants are reviewed as space permits.' },
            { school: 'Design', requirement: 'An ACT 21 (SAT 1060) and 3.25 GPA, or an ACT 24 (SAT 1160) and 3.0 GPA, plus a portfolio and essay. Without scores you get a holistic review. Priority deadline November 1, final deadline February 1.' },
            { school: 'Journalism and Mass Communications', requirement: 'A 3.25 high school GPA, or a 3.0 GPA with an ACT 21 (SAT 1060).' },
            { school: 'Music', requirement: 'An audition on your primary instrument or voice.' },
            { school: 'Nursing, Pharmacy and Health Professions', requirement: 'You enter these programs after two years of undergraduate study, with a minimum college GPA and prerequisite courses.' },
            { school: 'University Honors Program', requirement: 'Essay and short answer prompts with no minimum GPA or test score. Priority deadline November 1, standard deadline December 1, late consideration February 1.' },
        ],
        source: 'majorReq',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Freshman scholarship deadline', value: 'December 1' },
        rows: [
            { plan: 'Freshman scholarship deadline', deadline: 'December 1', decision: null, binding: false },
            { plan: 'FAFSA priority deadline', deadline: 'February 1', decision: null, binding: false },
        ],
        note: 'KU lists no application deadline for admission and publishes no decision dates; it says it strives to respond as quickly as possible to every complete application. No test scores are necessary to apply. The page carries no year.',
        source: 'apply',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'KU is test optional. You are assured admission to the College of Liberal Arts & Sciences with a transcript and either a cumulative high school GPA of 3.25 or higher (weighted or unweighted), or an ACT of at least 21 or SAT of at least 1060 with a 2.0 GPA. If you meet neither, KU reviews your application individually. KU highly encourages you to send ACT or SAT scores: they will not hurt your admission eligibility, but may be used in professional school admission decisions.',
        source: 'apply',
        cdsLine: 'The Common Data Set marks the SAT or ACT as not required for admission, but considered for some, with scores due by July 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'enrollment',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition (30 hours)', amounts: [11844, 31779] },
            { item: 'Required student and wellness fees', amounts: [1065, 1065] },
            { item: 'Housing and food (on campus)', amounts: [14370, 14370] },
            { item: 'Books and supplies', amounts: [778, 778] },
            { item: 'Transportation', amounts: [2600, 2600] },
            { item: 'Personal expenses', amounts: [2962, 2962] },
        ],
        totals: [33619, 53554],
        note: 'For a full-time undergraduate living on campus, fall and spring. Program course fees are extra and vary by school. KU\'s own totals ($33,725 and $53,660) add a $106 loan fee.',
        source: 'coa',
        netPrice: {
            average: 20004,
            byIncome: [
                { income: '$0 to $30,000', amount: 15379 },
                { income: '$30,001 to $48,000', amount: 15373 },
                { income: '$48,001 to $75,000', amount: 16017 },
                { income: '$75,001 to $110,000', amount: 20151 },
                { income: 'Over $110,000', amount: 24765 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from KU\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
